import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { TextInput } from '@/shared/ui/components/form/inputs';
import { useAgencyRequired } from './agency-required-context';
import type { PropertyFormValues } from './schema';
import { PROPERTY_FIELD_LIMITS } from './schema';

interface PropertyCodeFieldProps {
  form: UseFormReturn<PropertyFormValues>;
}

/**
 * The code a saved property carried when the form opened, but only once the
 * broker has changed it: that is when keys and folders need updating.
 *
 * @param code - The code currently typed.
 * @param initial - The code the form opened with.
 * @returns The previous code to warn about, or an empty string when there is nothing to warn.
 */
export function changedPreviousCode(code: string | undefined, initial: string | undefined): string {
  const previous = (initial ?? '').trim();
  return previous !== '' && (code ?? '').trim() !== previous ? previous : '';
}

/**
 * The agency's own property code input (optional).
 *
 * It is what the agency writes on its keys and folders, so changing a saved
 * one warns that those need updating. It is not checked for uniqueness: the
 * same code may sit on two keys.
 */
export function PropertyCodeField({ form }: PropertyCodeFieldProps) {
  const { t } = useTranslation('features/properties');
  const [code, initial] = useWatch({ control: form.control, name: ['property_code', 'property_code_initial'] });
  const previous = changedPreviousCode(code, initial);
  const required = useAgencyRequired('property_code');

  return (
    <Controller
      name="property_code"
      control={form.control}
      render={({ field, fieldState }) => (
        <div className="flex flex-col gap-(--space-1-5)">
          <TextInput
            label={t(required ? 'propertyDrawer.publication.codeLabelRequired' : 'propertyDrawer.publication.codeLabel')}
            required={required}
            value={field.value ?? ''}
            error={fieldState.error?.message}
            placeholder={t('propertyDrawer.publication.codePlaceholder')}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            maxLength={PROPERTY_FIELD_LIMITS.propertyCode.maxLength}
            data-field="property_code"
          />
          <Text as="span" className="text-(--text-tertiary)" style={{ fontSize: 'var(--text-meta)' }}>
            {t('propertyDrawer.publication.codeHint')}
          </Text>
          {previous && (
            <Text as="span" className="text-(--status-warning-text)" style={{ fontSize: 'var(--text-meta)' }}>
              {t('propertyDrawer.publication.codePreviousNotice', { code: previous })}
            </Text>
          )}
        </div>
      )}
    />
  );
}
